import type { CourseProgress } from "@/core/progress";
import { flattenLessons, type Course } from "@/core/types";
import { Icon } from "@/ui/components/Icon";
import { MacroPanel } from "@/ui/components/MacroPanel";
import { StatusRing } from "@/ui/components/Progress";
import { lessonStepsDone, lookupFor } from "@/ui/progressStats";
import { Breadcrumbs } from "./parts";
import { href } from "./useHashRoute";

interface WebLessonProps {
  course: Course;
  moduleId: string;
  lessonId: string;
  progress: CourseProgress;
}

/**
 * Eine Lektion zum Lesen: alle Schritte untereinander. Geprüft wird nur im
 * Seitenpanel – hier steht bei jeder Aufgabe, wo das geht.
 */
export function WebLesson({ course, moduleId, lessonId, progress }: WebLessonProps) {
  const lessons = flattenLessons(course);
  const index = lessons.findIndex(
    (entry) => entry.module.id === moduleId && entry.lesson.id === lessonId,
  );
  const current = lessons[index];

  if (!current) {
    return (
      <div className="w-page w-missing">
        <Breadcrumbs
          items={[
            { label: "Übersicht", to: href.home() },
            { label: course.title, to: href.course(course.id) },
          ]}
        />
        <h1>Diese Lektion gibt es nicht</h1>
        <p>
          Vielleicht wurde sie umbenannt. <a href={href.course(course.id)}>Zum {course.title}</a>
        </p>
      </div>
    );
  }

  const { module, lesson } = current;
  const lookup = lookupFor(progress);
  const done = lessonStepsDone(module, lesson, lookup);
  const previous = lessons[index - 1];
  const next = lessons[index + 1];
  const stepAnchor = (stepId: string) => `w-schritt-${stepId}`;

  return (
    <div className="w-page w-lesson-page" data-host={course.host}>
      <Breadcrumbs
        items={[
          { label: "Übersicht", to: href.home() },
          { label: course.title, to: href.course(course.id) },
          { label: module.title },
        ]}
      />

      <header className="w-title-row">
        <div>
          <h1>{lesson.title}</h1>
          <p className="w-title-row__sub num">
            {lesson.summary} {lesson.durationMinutes} Min. · {done} von {lesson.steps.length}{" "}
            Schritten erledigt.
          </p>
        </div>
      </header>

      <div className="w-grid">
        <ol className="w-steps">
          {lesson.steps.map((step) => {
            const stepDone = lookup.isStepDone(module.id, lesson.id, step.id);
            return (
              <li key={step.id} id={stepAnchor(step.id)} className="w-step">
                <header className="w-step__head">
                  <StatusRing fraction={stepDone ? 1 : 0} />
                  <h2>{step.title}</h2>
                  {stepDone ? <span className="pill pill--positive">Erledigt</span> : null}
                </header>

                <div className="w-step__body">
                  <div className="prose">
                    {step.explanation.map((paragraph) => (
                      <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                    ))}
                  </div>

                  {step.keyPoints && step.keyPoints.length > 0 ? (
                    <aside className="keypoints" aria-label="Auf einen Blick">
                      <h3 className="keypoints__title">Auf einen Blick</h3>
                      <ul>
                        {step.keyPoints.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    </aside>
                  ) : null}

                  {step.task ? (
                    <section className="task" aria-label="Aufgabe">
                      <h3 className="task__title">
                        <Icon name="task" />
                        Aufgabe
                      </h3>
                      <p className="task__instruction">{step.task.instruction}</p>
                      {step.task.hint ? (
                        <details className="disclosure disclosure--inline">
                          <summary>
                            <Icon name="bulb" />
                            <span>Tipp anzeigen</span>
                            <Icon name="chevronDown" className="disclosure__chevron" />
                          </summary>
                          <p className="disclosure__content">{step.task.hint}</p>
                        </details>
                      ) : null}
                      {step.task.macro ? (
                        <MacroPanel macro={step.task.macro} nurErsatz={step.task.macroNurErsatz} />
                      ) : null}
                      <p className="w-task-note">
                        <Icon name="info" size={14} />
                        Geprüft wird diese Aufgabe im Seitenpanel von {course.host}.
                      </p>
                    </section>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>

        <aside className="w-rail w-rail--sticky">
          <section className="w-panel" aria-labelledby="ziele-titel">
            <h2 className="w-panel__title" id="ziele-titel">
              Lernziele
            </h2>
            <ul className="w-goals">
              {lesson.learningGoals.map((goal) => (
                <li key={goal}>{goal}</li>
              ))}
            </ul>
          </section>

          <nav className="w-panel" aria-labelledby="schritte-titel">
            <h2 className="w-panel__title" id="schritte-titel">
              In dieser Lektion
            </h2>
            <ol className="w-toc">
              {lesson.steps.map((step) => (
                <li key={step.id}>
                  {/* Ein Knopf statt Ankerlink: der Hash trägt schon die Route. */}
                  <button
                    type="button"
                    onClick={() =>
                      document
                        .getElementById(stepAnchor(step.id))
                        ?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                  >
                    <StatusRing
                      fraction={lookup.isStepDone(module.id, lesson.id, step.id) ? 1 : 0}
                      size={16}
                    />
                    <span>{step.title}</span>
                  </button>
                </li>
              ))}
            </ol>
          </nav>
        </aside>

        <nav className="w-pager" aria-label="Weitere Lektionen">
          {previous ? (
            <a
              className="w-pager__link"
              href={href.lesson(course.id, previous.module.id, previous.lesson.id)}
            >
              <span className="w-pager__label">
                <Icon name="chevronLeft" size={14} />
                Vorherige Lektion
              </span>
              <span className="w-pager__title">{previous.lesson.title}</span>
            </a>
          ) : (
            <span />
          )}
          {next ? (
            <a
              className="w-pager__link w-pager__link--next"
              href={href.lesson(course.id, next.module.id, next.lesson.id)}
            >
              <span className="w-pager__label">
                Nächste Lektion
                <Icon name="chevronRight" size={14} />
              </span>
              <span className="w-pager__title">{next.lesson.title}</span>
            </a>
          ) : null}
        </nav>
      </div>
    </div>
  );
}
